import { useEffect, useState } from "react";
import { PermissionsUI } from "./Constants";
import moment from "moment";
import hash from "object-hash";

/**
 * Hook that checks the role of the logged user and renders the component based on the role
 * @param WrappedComponent
 * @returns
 */

/**
 * Recursively merges `obj` onto `initialObj`: plain objects are merged key by key,
 * arrays are unioned via compareArraysAndMerge, and every other value (including
 * moment instances, which are left intact rather than recursed into) is overwritten
 * by the value from `obj`.
 *
 * @param initialObj base object whose keys are preserved unless overridden
 * @param obj object whose keys take precedence
 * @returns a new merged object; neither input is mutated
 */
export function deepMergeObject(initialObj: any, obj: any) {
  const newObj = { ...initialObj };
  for (const key in obj) {
    const isArray = newObj[key] instanceof Array;
    if (
      typeof newObj[key] === "object" &&
      !isArray &&
      typeof obj[key] === "object" &&
      !moment.isMoment(obj[key])
    ) {
      newObj[key] = deepMergeObject(newObj[key], obj[key]);
    } else {
      if (isArray) {
        newObj[key] = compareArraysAndMerge(newObj[key], obj[key]);
      } else {
        newObj[key] = obj[key];
      }
    }
  }
  return newObj;
}

/**
 * Unions two arrays by content rather than reference: every element of `obj` is kept,
 * plus any element of `initialObj` whose content hash does not already appear in `obj`.
 * Elements present in both (by content hash) are not duplicated.
 *
 * @param initialObj array whose unique-by-content elements get appended to the result
 * @param obj array whose elements form the base of the result and take precedence
 * @returns a new array containing every element of `obj` followed by the elements of `initialObj` not already present in `obj` by content hash
 */
export function compareArraysAndMerge(initialObj: any[], obj: any[]) {
  //create copy of the second array (in general bigger than the first one)
  const newArray = [...obj];
  //hash every element in both arrays
  const initialObjHash = initialObj.map((obj) => hash(obj));
  const objHash = obj.map((obj) => hash(obj));
  //check if the hash of the element in the first array is present in the second array
  const diff = initialObjHash.filter((obj) => !objHash.includes(obj));
  //if the hash is not present, add the element to the second array based on the index of the hash
  diff.forEach((hash) => {
    const index = initialObjHash.indexOf(hash);
    newArray.push(initialObj[index]);
  });
  return newArray;
}

const withRole = (WrappedComponent: any) => {
  return function useWithRole(props: any) {
    const [newProps, setNewProps] = useState<any>(props);

    /**
     * Check the role of the logged user and and get props from PermissionsUI based on the priority of the role
     */
    useEffect(() => {
      if (!window.localStorage.getItem("permissions")) {
        window.location.reload();
        return;
      }

      const userRolesJson: any = window.localStorage.getItem("permissions");
      const userRoles: any = userRolesJson.split(",");
      //get role with highest priority (lower value in this case)

      //get props from PermissionsUI based on the priority of the role
      let updatedProps = props;
      userRoles.forEach((element: any, index: any) => {
        const ui = PermissionsUI[userRoles[index]];
        let uiProps;

        // if (ui) uiProps = ui[props.name];
        if (ui) uiProps = ui[props.name];

        //merge props from PermissionsUI with the props of the component

        updatedProps = deepMergeObject(updatedProps, uiProps);
      });

      // if (uiProps)
      setNewProps(updatedProps);
      // else setNewProps(null);
    }, []);

    return newProps ? <WrappedComponent {...newProps} /> : null;
  };
};

export default withRole;
